import {
    SliceFactory,
    FetchCollectionData,
    ApiCollectionResponse,
    FetchItemDataType,
} from '@europrocurement/l2d-redux-utils';
import {
    ConfigurationParameters as DossierOpenSearchApiConfigurationParameters,
    Configuration,
    type DossierOpenSearchJsonldOpensearchDossierRead,
    DossierOpenSearchApi,
    type DossierOpenSearchApiApiOpensearchDossiersGetCollectionRequest,
    type DossierOpenSearchApiApiOpensearchDossiersIdGetRequest,
    type FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup,
    type DossierOpenSearchCombinedJsonld,
} from '../../../openApi/ApiFormalite';
import { DOSSIERS_OPEN_SEARCH_SLICE_NAME, FORMALITES_REDUCER_NAME } from '../constants';

export type DossierOpenSearchApiObject = DossierOpenSearchJsonldOpensearchDossierRead;

export type NestedDossierFormaliteOpenSearch = {
    id: number;
    libelle: string;
    prestationId: Array<number>;
    articleIds: Array<number>;
    documentsCount: number;
    missingDocumentsCount: number;
    optionsCount: number;
    categoryId: number;
};

export type NestedDossierProcessesOpenSearch = {
    id: number;
    validatorId: number;
    validatorLabel?: string;
    validatorTypeId: number;
    validatorTypeLabel?: string;
    prestations: Array<number>;
    articles: Array<number>;
    statusId: number;
    statusLabel: string;
    statusCode: string;
    statusSortOrder: string;
    statusFlag: 100 | 200 | 300 | 400 | 500;
    externalUrl: string;
    identifier: string;
    createdAt: Date;
};

export type CustomDossierOpenSearch = {
    validationProcesses: Array<NestedDossierProcessesOpenSearch>;
    formalites: Array<NestedDossierFormaliteOpenSearch>;
} & Record<string, unknown> &
    Omit<DossierOpenSearchCombinedJsonld, 'formalites'>;

// Previous type
export type DossierOpenSearch = {
    siblings?: CustomDossierOpenSearch[];
    notes?: FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup[];
} & CustomDossierOpenSearch;

// Schema Open api a mettre à jour
// export type DossierOpenSearch = {
//     siblings?: DossierOpenSearchCombinedJsonld[];
//     notes?: FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup[];
//     formalites: Array<NestedDossierFormaliteOpenSearch>;
//     validationProcesses: Array<NestedDossierProcessesOpenSearch>;
// } & DossierOpenSearchCombinedJsonld &
//     Omit<DossierOpenSearchApiObject, 'formalites'>;

export const mapperOpenSearchDossier = function (dossier: DossierOpenSearch) {
    return {
        ...dossier,
    } as DossierOpenSearch;
};

export function createDossierOpenSearchSlice(
    configuration: DossierOpenSearchApiConfigurationParameters,
) {
    const conf = new Configuration(configuration);
    const api = new DossierOpenSearchApi(conf);

    const fetchDossierOpenSearchCollection: FetchCollectionData<DossierOpenSearch> = ({
        search,
        pagination,
        orders,
        filters,
        preFilters,
    }) => {
        const options = undefined;

        let startDate: string | undefined;
        let endDate: string | undefined;
        let q: string | undefined;

        let orderNumero: 'asc' | 'desc' | undefined;
        let orderContactNom: 'asc' | 'desc' | undefined;
        let orderSocieteDenomination: 'asc' | 'desc' | undefined;
        let orderDateCreation: 'asc' | 'desc' | undefined;
        let orderStatutLibelle: 'asc' | 'desc' | undefined;
        let orderFormalisteNom: 'asc' | 'desc' | undefined;
        let orderPrescripteurLibelle: 'asc' | 'desc' | undefined;
        let orderOffreLibelle: 'asc' | 'desc' | undefined;
        let orderGreffeLibelle: 'asc' | 'desc' | undefined;

        const {
            groupId,
            collectionId,
            clientId,
            formalisteId,
            workGroupId,
            formaliteId,
            numero,
            societeReference,
            hasMissingRequiredDocuments,
            hasMessageUnreadClient,
            hasMessageUnreadInterne,
            demo,
            contactId,
            prescriberId,
            societeId,
            statutValideurId,
            offerId,
            greffeId,
        } = filters;

        const dateCreation = filters.dateCreation as
            | { startDate?: string; endDate?: string }
            | undefined;

        const { statutId, stepId, preFilterFormalisteId } = preFilters;
        let { critical } = preFilters;
        if (critical === 1) {
            critical = true;
        }

        if (dateCreation?.startDate) {
            startDate = dateCreation.startDate;
        }

        if (dateCreation?.endDate) {
            endDate = dateCreation.endDate;
        }

        if (search && search !== '') {
            q = search;
        }

        orders.forEach((order) => {
            if (order.field === 'numero') {
                orderNumero = order.value;
            }

            if (order.field === 'societeDenomination') {
                orderSocieteDenomination = order.value;
            }

            if (order.field === 'contactNom') {
                orderContactNom = order.value;
            }

            if (order.field === 'dateCreation') {
                orderDateCreation = order.value;
            }

            if (order.field === 'statutLibelle') {
                orderStatutLibelle = order.value;
            }

            if (order.field === 'formaliste') {
                orderFormalisteNom = order.value;
            }

            if (order.field === 'prescripteurLibelle') {
                orderPrescripteurLibelle = order.value;
            }

            if (order.field === 'offreLibelle') {
                orderOffreLibelle = order.value;
            }

            if (order.field === 'greffeLibelle') {
                orderGreffeLibelle = order.value;
            }
        });

        const requestParameters: DossierOpenSearchApiApiOpensearchDossiersGetCollectionRequest = {
            page: pagination.page + 1,
            itemsPerPage: pagination.itemsPerPage,
            groupId: [groupId as number],
            collectionId: [collectionId as number],
            clientId: clientId as number[],
            workGroupId: [workGroupId as number],
            formalisteId: [(formalisteId || preFilterFormalisteId) as number],
            formaliteId: formaliteId as number[],
            statutId: [statutId as number],
            stepId: [stepId] as number[],
            critical: critical as boolean,
            numero: numero as string[],
            societeId: [societeId as number],
            societeReference: [societeReference as string],
            startDate: startDate ? new Date(startDate).toDateString() : undefined,
            endDate: endDate ? new Date(endDate).toDateString() : undefined,
            hasMissingRequiredDocuments:
                hasMissingRequiredDocuments !== undefined
                    ? Boolean(hasMissingRequiredDocuments)
                    : undefined,
            hasMessageUnreadClient:
                hasMessageUnreadClient !== undefined ? Boolean(hasMessageUnreadClient) : undefined,
            hasMessageUnreadInterne:
                hasMessageUnreadInterne !== undefined
                    ? Boolean(hasMessageUnreadInterne)
                    : undefined,
            demo: demo !== undefined ? Boolean(demo) : undefined,
            contactId: [contactId as number],
            prescriberId: [prescriberId as number],
            offerId: [offerId as number],
            statutValideurId: [statutValideurId as number],
            greffeId: [greffeId as number],
            q,
            orderNumero,
            orderSocieteDenomination,
            orderContactNom,
            orderDateCreation,
            orderStatutLibelle,
            orderFormalisteNom,
            orderPrescripteurLibelle,
            orderOffreLibelle,
            orderGreffeLibelle,
        };

        return api
            .apiOpensearchDossiersGetCollection(requestParameters, options)
            .then((res) => res.data) as Promise<ApiCollectionResponse<DossierOpenSearch>>;
    };

    const fetchDossierOpenSearchItem: FetchItemDataType<DossierOpenSearch> = ({ idItem }) => {
        const requestParameters: DossierOpenSearchApiApiOpensearchDossiersIdGetRequest = {
            id: String(idItem),
        };

        return api
            .apiOpensearchDossiersIdGet(requestParameters)
            .then((res) => res.data) as Promise<DossierOpenSearch>;
    };

    const { slice: dossierOpenSearchSlice, dataSourcesThunks: dossierOpenSearchDataSourcesThunks } =
        SliceFactory.createSlice<DossierOpenSearch, DossierOpenSearch>(
            DOSSIERS_OPEN_SEARCH_SLICE_NAME,
            FORMALITES_REDUCER_NAME,
            [],
            fetchDossierOpenSearchCollection,
            fetchDossierOpenSearchItem,
            mapperOpenSearchDossier,
            {},
            undefined,
            [{ field: 'dateCreation', value: 'desc' }],
        );

    return { dossierOpenSearchSlice, dossierOpenSearchDataSourcesThunks };
}
